import { Box, ButtonBase, IconButton, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { colors, theme } from '@rumblefish/ui';
import { Section } from '@rumblefish/ui/Rumblefish23Theme';
import clsx from 'clsx';
import { motion, useAnimationControls } from 'framer-motion';
import { useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import CaseStudiesGrid from '../CaseStudiesGrid';
import { FILTERS_CONTAINER_HEIGHT } from './FiltersContainer.constants';
import type { FiltersContainerProps } from './FiltersContainer.types';

const PREFIX = 'FiltersContainer';

const classes = {
  root: `${PREFIX}-root`,
  filterText: `${PREFIX}-filterText`,
  carouselWrapper: `${PREFIX}-carouselWrapper`,
  scrollTrack: `${PREFIX}-scrollTrack`,
  chip: `${PREFIX}-chip`,
  chipActive: `${PREFIX}-chipActive`,
  arrowButton: `${PREFIX}-arrowButton`,
  filterContainer: `${PREFIX}-filterContainer`,
  filterMenuContainer: `${PREFIX}-filterMenuContainer`,
};

const StyledContent = styled(motion.div)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(8, 0, 0),
  width: '100%',
  alignItems: 'center',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3, 0, 0),
    position: 'fixed',
    top: '65px',
    left: '0',
    width: '100%',
    background: theme.palette.background.default,
    zIndex: 100,
  },
}));

const StyledFiltersContainer = styled('div')(({ theme }) => ({
  [`& .${classes.filterText}`]: {
    color: theme.palette.invertedContent?.TETRIARY,
    fontSize: '20px',
    fontStyle: 'normal',
    fontWeight: 900,
    lineHeight: '20px',
    letterSpacing: '0em',
    textAlign: 'left',
    marginBottom: theme.spacing(2),
  },

  [`& .${classes.filterMenuContainer}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'center',
    alignItems: 'center',
    gap: theme.spacing(1),
    zIndex: 1,
  },

  [`& .${classes.filterContainer}`]: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  },

  [`& .${classes.carouselWrapper}`]: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    paddingBottom: theme.spacing(3),
    gap: theme.spacing(1),
  },

  [`& .${classes.scrollTrack}`]: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'nowrap',
    overflowX: 'auto',
    gap: theme.spacing(1),
    scrollBehavior: 'smooth',
    msOverflowStyle: 'none',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': {
      display: 'none',
    },
  },

  [`& .${classes.arrowButton}`]: {
    flexShrink: 0,
    width: '32px',
    height: '32px',
    color: theme.palette.invertedContent?.TETRIARY,
    border: `1px solid ${theme.palette.blueColors?.PLUS_2}`,
    boxShadow: `0px 0px 2px 1px ${theme.palette.blueColors?.PLUS_2}`,
    borderRadius: '50%',
    padding: 0,
    transition: 'background-color 0.2s ease, color 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.blueColors?.PLUS_1,
      color: colors.white,
      boxShadow: 'none',
    },
    [theme.breakpoints.down('md')]: {
      display: 'none',
    },
  },

  [`& .${classes.chip}`]: {
    flexShrink: 0,
    borderRadius: '100px',
    padding: '6px 16px',
    fontFamily: 'Satoshi',
    fontSize: '14px',
    fontWeight: 600,
    lineHeight: '17px',
    whiteSpace: 'nowrap',
    color: theme.palette.invertedContent?.TETRIARY,
    border: `1px solid ${theme.palette.blueColors?.PLUS_2}`,
    boxShadow: `0px 0px 2px 1px ${theme.palette.blueColors?.PLUS_2}`,
    transition: 'background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease',
    '&:hover': {
      backgroundColor: theme.palette.blueColors?.PLUS_1,
      color: colors.white,
      boxShadow: 'none',
    },
  },

  [`& .${classes.chipActive}`]: {
    backgroundColor: theme.palette.blueColors?.PLUS_1,
    color: colors.white,
    boxShadow: 'none',
  },
}));

const SCROLL_STEP = 200;

export const FiltersContainer: React.FC<FiltersContainerProps> = ({
  filters,
  presentFilter,
  pagination,
  shallowRedirect,
  data,
  showSwipeIcon,
  caseStudiesGridEl,
  hideFilters,
}) => {
  const filtersContainerEl = useRef<HTMLDivElement>(null);
  const scrollTrackEl = useRef<HTMLDivElement>(null);

  const handleFilterOnClick = useCallback(
    (newValue: string) => {
      newValue === presentFilter
        ? shallowRedirect({ page: pagination })
        : shallowRedirect({ filter: newValue, page: 1 });
    },
    [pagination, shallowRedirect, presentFilter]
  );

  const scrollLeft = useCallback(() => {
    if (scrollTrackEl.current)
      scrollTrackEl.current.scrollLeft -= SCROLL_STEP;
  }, []);

  const scrollRight = useCallback(() => {
    if (scrollTrackEl.current)
      scrollTrackEl.current.scrollLeft += SCROLL_STEP;
  }, []);

  const filtersContainerVariants = {
    hidden: {
      y: FILTERS_CONTAINER_HEIGHT * -1,
      transition: { ease: 'easeInOut' },
    },
    visible: {
      y: 0,
      transition: { ease: 'easeInOut' },
    },
  };
  const controls = useAnimationControls();

  useEffect(() => {
    controls.start(hideFilters ? 'hidden' : 'visible');
  }, [hideFilters, controls]);

  return (
    <>
      {caseStudiesGridEl.current &&
        createPortal(
          <CaseStudiesGrid
            pagination={pagination}
            data={data}
            filter={presentFilter}
            shallowRedirect={shallowRedirect}
            filtersContainerHeight={FILTERS_CONTAINER_HEIGHT}
            showSwipeIcon={showSwipeIcon}
          />,
          caseStudiesGridEl.current
        )}
      <StyledFiltersContainer className={clsx(classes.root)}>
        <Section
          width="normal"
          sx={{
            position: 'relative',
          }}>
          <Box
            sx={{
              display: 'none',
              height: filtersContainerEl?.current?.offsetHeight ?? '64px',
              transition: 'height 0.3s ease',
              [theme.breakpoints.down('md')]: {
                display: 'block',
              },
            }}
          />
          <StyledContent
            ref={filtersContainerEl}
            id="filtersContainer"
            initial={'visible'}
            variants={filtersContainerVariants}
            animate={controls}>
            <Stack className={classes.carouselWrapper}>
              <IconButton
                className={classes.arrowButton}
                onClick={scrollLeft}
                aria-label="scroll filters left"
                disableRipple>
                ‹
              </IconButton>
              <Box
                ref={scrollTrackEl}
                className={classes.scrollTrack}>
                {filters.map((filter) => (
                  <ButtonBase
                    key={filter.value}
                    className={clsx(classes.chip, {
                      [classes.chipActive]: presentFilter === filter.value,
                    })}
                    onClick={() => handleFilterOnClick(filter.value)}>
                    {filter.label}
                  </ButtonBase>
                ))}
              </Box>
              <IconButton
                className={classes.arrowButton}
                onClick={scrollRight}
                aria-label="scroll filters right"
                disableRipple>
                ›
              </IconButton>
            </Stack>
          </StyledContent>
        </Section>
      </StyledFiltersContainer>
    </>
  );
};
